/**
 * @description Products workspace with product/category/vendor/discount tables and edit modals.
 * @dependencies React Router, Headless UI, useProducts, product/category/vendor/discount components.
 * @notes Product pricing and variants submit through one atomic product service mutation.
 *        Discount CRUD uses `/api/discounts` from Wave 1. Soft-delete is status=inactive.
 */

import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useProducts } from '../hooks/useProducts';
import ProductForm from './products/ProductForm';
import ProductsTable from './products/ProductsTable';
import CategoryForm from './products/CategoryForm';
import CategoriesTable from './products/CategoriesTable';
import VendorForm from './products/VendorForm';
import VendorsTable from './products/VendorsTable';
import DiscountForm from './products/DiscountForm';
import DiscountsTable from './products/DiscountsTable';
import PermissionGatedDropdown from './shared/PermissionGatedDropdown';
import { PermissionGuard } from './shared/PermissionGate';
import { PERMISSIONS } from '../constants/permissionMappings';

const Products = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('products');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const {
    products,
    categories,
    vendors,
    discounts,
    loading,
    error,
    setError,
    productOperations,
    categoryOperations,
    vendorOperations,
    discountOperations,
    refetch
  } = useProducts();

  // Handle URL query parameter for opening modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openModalParam = searchParams.get('openModal');
    
    if (openModalParam === 'product') {
      openModal('product');
      window.history.replaceState({}, '', '/products');
    }
  }, [location]);

  // Modal handlers
  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalType(null);
    setEditingItem(null);
  };

  // Form submission handlers
  const handleProductSubmit = async (formData) => {
    try {
      const { variants, ...productData } = formData;

      if (editingItem) {
        await productOperations.update(
          editingItem.id,
          { ...productData, variants },
          { refetch: false }
        );
      } else {
        await productOperations.create({ ...productData, variants });
        closeModal();
        return;
      }

      await refetch();
      closeModal();
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleCategorySubmit = async (formData) => {
    try {
      await categoryOperations.create(formData);
      closeModal();
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleVendorSubmit = async (formData) => {
    try {
      if (editingItem) {
        await vendorOperations.update(editingItem.id, formData);
      } else {
        await vendorOperations.create(formData);
      }
      closeModal();
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleDiscountSubmit = async (formData) => {
    try {
      if (editingItem) {
        await discountOperations.update(editingItem.id, formData);
      } else {
        await discountOperations.create(formData);
      }
      closeModal();
    } catch (error) {
      // Error is handled in the hook and DiscountForm field errors
      throw error;
    }
  };

  const handleDiscountEdit = async (discount) => {
    try {
      const details = await discountOperations.getDetails(discount.id);
      openModal('discount', details || discount);
    } catch (error) {
      setError('Failed to load discount details for editing.');
    }
  };

  const handleProductEdit = async (product) => {
    try {
      const details = await productOperations.getDetails(product.id);
      openModal('product', details || product);
    } catch (error) {
      setError('Failed to load product details for editing.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-[2000px] w-full mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Products</h1>
          <PermissionGatedDropdown
            items={[
              { label: 'New Product', permission: PERMISSIONS.products.create, onClick: () => openModal('product') },
              { label: 'New Category', permission: PERMISSIONS.categories.create, onClick: () => openModal('category') },
              { label: 'New Vendor', permission: PERMISSIONS.vendors.create, onClick: () => openModal('vendor') },
              { label: 'New Discount', permission: PERMISSIONS.discounts.create, onClick: () => openModal('discount') },
            ]}
            buttonLabel="Create"
          />
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-6">
              {['products', 'categories', 'vendors', 'discounts'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors capitalize ${
                    activeTab === tab
                      ? 'border-brand text-brand'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'products' && (
          <ProductsTable
            products={products}
            categories={categories}
            onEdit={handleProductEdit}
            onDelete={productOperations.delete}
            onCreateClick={() => openModal('product')}
          />
        )}
        
        {activeTab === 'categories' && (
          <CategoriesTable
            categories={categories}
            onDelete={categoryOperations.delete}
            onCreateClick={() => openModal('category')}
          />
        )}
        
        {activeTab === 'vendors' && (
          <VendorsTable
            vendors={vendors}
            onEdit={(vendor) => openModal('vendor', vendor)}
            onDelete={vendorOperations.delete}
            onCreateClick={() => openModal('vendor')}
          />
        )}

        {activeTab === 'discounts' && (
          <PermissionGuard permission={PERMISSIONS.discounts.view}>
            <DiscountsTable
              discounts={discounts}
              onEdit={handleDiscountEdit}
              onDelete={discountOperations.delete}
              onCreateClick={() => openModal('discount')}
            />
          </PermissionGuard>
        )}
        
        {/* Modal */}
        <Modal isOpen={modalOpen} onClose={closeModal} title={getModalTitle()}>
          {modalType === 'product' && (
            <ProductForm
              initialData={editingItem}
              categories={categories}
              vendors={vendors}
              onSubmit={handleProductSubmit}
              onCancel={closeModal}
            />
          )}
          {modalType === 'category' && (
            <CategoryForm
              initialData={editingItem}
              onSubmit={handleCategorySubmit}
              onCancel={closeModal}
            />
          )}
          {modalType === 'vendor' && (
            <VendorForm
              initialData={editingItem}
              onSubmit={handleVendorSubmit}
              onCancel={closeModal}
            />
          )}
          {modalType === 'discount' && (
            <DiscountForm
              initialData={editingItem}
              products={products}
              onSubmit={handleDiscountSubmit}
              onCancel={closeModal}
            />
          )}
        </Modal>
      </div>
    </div>
  );

  function getModalTitle() {
    if (modalType === 'product') {
      return editingItem ? 'Edit Product' : 'Create New Product';
    }
    if (modalType === 'category') {
      return 'Create New Category';
    }
    if (modalType === 'vendor') {
      return editingItem ? 'Edit Vendor' : 'Create New Vendor';
    }
    if (modalType === 'discount') {
      return editingItem ? 'Edit Discount' : 'Create New Discount';
    }
    return '';
  }
};

/**
 * @description Enterprise modal component using @headlessui/react Dialog + Transition
 *
 * Key features:
 * - Accessible modal with proper focus management
 * - Backdrop blur with smooth transitions
 * - Unsaved changes protection with nested confirm dialog
 * - Responsive design with max-width constraints
 *
 * @dependencies
 * - @headlessui/react: Dialog, Transition components
 * - @heroicons/react: XMarkIcon
 */
const Modal = ({ isOpen, onClose, title, children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const cancelCloseRef = useRef(null);

  const handleClose = () => {
    if (showConfirmClose) return;
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleForceClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  // Clone children to inject hasUnsavedChanges setter
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onFormChange: (hasChanges) => setHasUnsavedChanges(hasChanges)
      });
    }
    return child;
  });

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-4xl max-h-[90vh] flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900">
                      {title}
                    </Dialog.Title>
                    <button
                      onClick={handleClose}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {childrenWithProps}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirm Close Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Unsaved Changes
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have unsaved changes. Are you sure you want to close without saving?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      ref={cancelCloseRef}
                      className="inline-flex w-full justify-center rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-200 sm:col-start-1"
                      onClick={() => setShowConfirmClose(false)}
                    >
                      Keep Editing
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors duration-200 sm:col-start-2"
                      onClick={handleForceClose}
                    >
                      Discard Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default Products;